// apps/scheduler-worker/src/components/TaskCard.tsx
import { ChevronRight, Clock, CalendarDays } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { TaskStatusBadge } from './TaskStatusBadge';
import { formatDuration } from 'scheduler-lib';
import type { WorkerTask } from '../lib/api';
import { getLaborBucketLabel } from '../lib/api';
import { getTaskProgress, PROGRESS_COLORS } from '../lib/progress';

interface TaskCardProps {
  task: WorkerTask;
  isActive?: boolean;
  onClick?: () => void;
}

export function TaskCard({ task, isActive, onClick }: TaskCardProps) {
  const progress = task.status === 'in_progress'
    ? getTaskProgress(task.actualMinutes, task.estimatedMinutes)
    : null;

  return (
    <button
      onClick={onClick}
      className={`w-full text-left bg-white rounded-lg border p-4 transition-all ${
        isActive
          ? 'border-green-500 ring-2 ring-green-200'
          : 'border-gray-200 hover:border-gray-300 hover:shadow-sm'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
              {getLaborBucketLabel(task.laborBucket)}
            </span>
            <TaskStatusBadge status={task.status} />
          </div>
          <p className="font-mono text-sm text-gray-500 mt-1">
            {task.partNumber}
            {task.gpSalesOrderNumber ? ` · ${task.gpSalesOrderNumber}` : ''}
          </p>
          <p className="text-sm font-medium text-gray-900 truncate mt-0.5">
            {task.projectName ?? task.gpSalesOrderNumber}
          </p>
        </div>
        <ChevronRight className="h-5 w-5 text-gray-400 flex-shrink-0" />
      </div>

      <div className="mt-3 pt-3 border-t border-gray-100">
        {progress?.showBar ? (
          <div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div
                className={`h-full rounded-full ${PROGRESS_COLORS[progress.color]}`}
                style={{ width: `${Math.min(progress.percentage, 100)}%` }}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1.5">{progress.label}</p>
          </div>
        ) : (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <CalendarDays className="h-3.5 w-3.5" />
              <span>
                {task.scheduledStart
                  ? format(parseISO(task.scheduledStart), 'EEE, MMM d')
                  : 'Not scheduled'}
              </span>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <Clock className="h-3.5 w-3.5" />
              <span>Est: {formatDuration(task.estimatedMinutes)}</span>
            </div>
          </div>
        )}
      </div>
    </button>
  );
}
